export const MAX_PRODUCT_PHOTOS = 12;

export interface ProductPhotoState {
  photo: string;
  photos: string[];
}

export type ProductPhotoMoveDirection = "left" | "right";

interface NormalizeProductPhotoStateInput {
  photo?: string | null;
  photos?: string[];
  maxCount?: number;
}

function normalizePhotoValue(value: unknown): string {
  return String(value || "").trim();
}

function clampMaxCount(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return MAX_PRODUCT_PHOTOS;
  }

  return Math.max(1, Math.floor(value));
}

export function isValidPhotoUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function normalizeProductPhotoState(
  input: NormalizeProductPhotoStateInput,
): ProductPhotoState {
  const maxCount = clampMaxCount(input.maxCount);
  const uniquePhotos = new Set<string>();
  const photos: string[] = [];

  const primaryPhoto = normalizePhotoValue(input.photo);
  if (primaryPhoto) {
    uniquePhotos.add(primaryPhoto);
    photos.push(primaryPhoto);
  }

  if (Array.isArray(input.photos)) {
    input.photos.forEach((item) => {
      const photo = normalizePhotoValue(item);
      if (!photo || uniquePhotos.has(photo)) {
        return;
      }

      uniquePhotos.add(photo);
      photos.push(photo);
    });
  }

  const normalizedPhotos = photos.slice(0, maxCount);

  return {
    photo: normalizedPhotos[0] || "",
    photos: normalizedPhotos,
  };
}

export function addProductPhoto(
  state: ProductPhotoState,
  value: string,
  options: { makePrimary?: boolean; maxCount?: number } = {},
): ProductPhotoState {
  const normalizedValue = normalizePhotoValue(value);
  const baseState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
    maxCount: options.maxCount,
  });

  if (!normalizedValue) {
    return baseState;
  }

  const existingIndex = baseState.photos.indexOf(normalizedValue);
  let nextPhotos = baseState.photos;

  if (existingIndex === -1) {
    const maxCount = clampMaxCount(options.maxCount);
    if (baseState.photos.length >= maxCount) {
      return baseState;
    }

    nextPhotos = [...baseState.photos, normalizedValue];
  }

  if (options.makePrimary) {
    const targetIndex = nextPhotos.indexOf(normalizedValue);
    return setProductPhotoAsMain(
      { photo: baseState.photo, photos: nextPhotos },
      targetIndex,
    );
  }

  return normalizeProductPhotoState({
    photo: baseState.photo,
    photos: nextPhotos,
    maxCount: options.maxCount,
  });
}

export function removeProductPhoto(
  state: ProductPhotoState,
  index: number,
  maxCount?: number,
): ProductPhotoState {
  const baseState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
    maxCount,
  });

  if (index < 0 || index >= baseState.photos.length) {
    return baseState;
  }

  const nextPhotos = baseState.photos.filter(
    (_, itemIndex) => itemIndex !== index,
  );

  return normalizeProductPhotoState({
    photo: nextPhotos[0] || "",
    photos: nextPhotos,
    maxCount,
  });
}

export function setProductPhotoAsMain(
  state: ProductPhotoState,
  index: number,
  maxCount?: number,
): ProductPhotoState {
  const baseState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
    maxCount,
  });

  if (index <= 0 || index >= baseState.photos.length) {
    return baseState;
  }

  const mainPhoto = baseState.photos[index];
  const remaining = baseState.photos.filter(
    (_, itemIndex) => itemIndex !== index,
  );

  return normalizeProductPhotoState({
    photo: mainPhoto,
    photos: remaining,
    maxCount,
  });
}

export function moveProductPhoto(
  state: ProductPhotoState,
  index: number,
  direction: ProductPhotoMoveDirection,
  maxCount?: number,
): ProductPhotoState {
  const baseState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
    maxCount,
  });

  if (index < 0 || index >= baseState.photos.length) {
    return baseState;
  }

  const nextIndex = direction === "left" ? index - 1 : index + 1;
  if (nextIndex < 0 || nextIndex >= baseState.photos.length) {
    return baseState;
  }

  const nextPhotos = [...baseState.photos];
  const temp = nextPhotos[nextIndex];
  nextPhotos[nextIndex] = nextPhotos[index];
  nextPhotos[index] = temp;

  return normalizeProductPhotoState({
    photo: nextPhotos[0] || "",
    photos: nextPhotos,
    maxCount,
  });
}
